import {
  useGetTicketTypesByEventId,
  useValidateDiscountCode,
} from '@sunrise-ui/api-client';
import {
  AuthFlowError,
  Button,
  ButtonVariants,
  Colors,
  Loader,
  TextInput,
  TextReg,
  useCheckoutModalStore,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { useOrderCalculations } from '../hooks/useOrderCalculations';
import { useCheckoutFormStore } from '../stores/useCheckoutFormStore';

type Props = {
  ticketQuantities: Map<string, number>;
};

export function DiscountCodeInput({ ticketQuantities }: Props) {
  const styles = useDynamicStyles(createStyles);
  const { appliedPromo, setAppliedPromo } = useCheckoutFormStore();
  const { eventId, initialPromoCode } = useCheckoutModalStore();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [isExpanded, setIsExpanded] = useState(!!initialPromoCode);
  const hasAttemptedInitialPromo = useRef(false);
  const { mutateAsync: validateDiscount, isPending } =
    useValidateDiscountCode();

  const { data: ticketTypes } = useGetTicketTypesByEventId({
    eventId,
  });

  const { hasEligibleTickets } = useOrderCalculations({
    ticketQuantities,
    appliedPromo,
  });

  // Shared function to validate and apply a discount code
  const applyDiscountCode = useCallback(
    async (discountCode: string) => {
      if (!ticketTypes) return;

      setError(undefined);

      try {
        const result = await validateDiscount({
          discountCode: discountCode.trim(),
          ticketTypeIds: ticketTypes.map((type) => type.id),
        });

        if (!result || result.length === 0) {
          setError('Invalid discount code');
          return;
        }

        setAppliedPromo(result[0]);
        setError(undefined);
        setCode('');
      } catch (error) {
        if (error instanceof AuthFlowError) return;
        setError('There was an error. Please try again');
      }
    },
    [ticketTypes, validateDiscount, setAppliedPromo],
  );

  // Auto-apply promo code from URL if present (only once)
  useEffect(() => {
    if (initialPromoCode && ticketTypes && !hasAttemptedInitialPromo.current) {
      hasAttemptedInitialPromo.current = true;
      setCode(initialPromoCode);
      void applyDiscountCode(initialPromoCode);
    }
  }, [initialPromoCode, ticketTypes, applyDiscountCode]);

  const handleApply = async () => {
    if (!code.trim()) {
      setError('Please enter a discount code');
      return;
    }

    await applyDiscountCode(code);
  };

  // Check if discount is valid but doesn't apply to any selected tickets
  const discountWarning =
    appliedPromo && !hasEligibleTickets
      ? 'This code is not valid for your selected tickets'
      : null;

  if (!isExpanded) {
    return (
      <View style={styles.collapsedContainer}>
        <TextReg style={styles.expandText} onPress={() => setIsExpanded(true)}>
          Have a discount code?
        </TextReg>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
    >
      <View style={styles.inputAndButtonContainer}>
        <TextInput
          label="Discount Code"
          showLabel={false}
          value={code}
          onChange={(value) => {
            setCode(value);
            setError(undefined);
          }}
          placeholder="Discount Code"
          disabled={isPending}
          style={styles.input}
          dense={true}
        />
        <Button
          variant={ButtonVariants.PURPLE}
          onPress={handleApply}
          disabled={!code.trim() || isPending}
          contentStyle={styles.applyButtonContent}
        >
          {isPending ? <Loader /> : 'Apply'}
        </Button>
      </View>
      {error ? (
        <TextReg style={styles.errorText}>{error}</TextReg>
      ) : discountWarning ? (
        <TextReg style={styles.warningText}>{discountWarning}</TextReg>
      ) : null}
    </KeyboardAvoidingView>
  );
}

const createStyles = () =>
  StyleSheet.create({
    inputAndButtonContainer: {
      flexDirection: 'row',
      gap: 12,
      alignItems: 'center',
      flex: 1,
    },
    input: {
      flex: 1,
    },
    applyButtonContent: {
      height: 40,
      minWidth: 100,
    },
    errorText: {
      fontSize: 12,
      marginTop: 8,
      marginLeft: 16,
      color: Colors.orange0,
    },
    warningText: {
      fontSize: 12,
      marginTop: 8,
      marginLeft: 16,
      color: Colors.yellow0,
    },
    collapsedContainer: {
      alignItems: 'flex-end',
    },
    expandText: {
      color: Colors.purple1,
    },
  });
